// src/components/friendship/FriendshipVibeFeatures.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, Target, Users, Heart, Shield, Compass, 
  Zap, Star, Sparkles, Award, BarChart3, Gamepad2 
} from 'lucide-react';
import Card from '../shared/Card';

const FriendshipVibeFeatures = () => {
  const features = [
    {
      icon: Brain,
      title: 'Friendship Vibe Check',
      description: 'Discover your unique friendship style across 6 archetypes. Understand both your strengths and the patterns that might be holding you back.',
      color: 'from-teal-500 to-cyan-500',
      emoji: '🧠',
      vibe: 'Your friendship DNA revealed',
    },
    {
      icon: Target,
      title: 'Pattern Recognition', 
      description: "Identify toxic patterns like people-pleasing, drama attraction, or possessiveness - and get concrete steps to change them.",
      color: 'from-coral-500 to-pink-500',
      emoji: '🎯',
      vibe: 'Spot the red flags early',
    },
    {
      icon: Users,
      title: 'Friend Group Patterns',
      description: 'Notice the roles people fall into in your groups, and where you feel most like yourself. No scores on your friends.',
      color: 'from-gold-500 to-orange-500',
      emoji: '👥',
      vibe: 'Read the room, kindly',
    },
    {
      icon: Heart,
      title: 'Emotional Intelligence Tools', 
      description: 'Build skills in boundary-setting, conflict resolution, and emotional regulation through interactive practice scenarios.',
      color: 'from-pink-500 to-red-500',
      emoji: '💜',
      vibe: "EQ skills that actually stick",
    },
    {
      icon: BarChart3,
      title: 'Growth Tracking',
      description: 'Watch your friendship patterns transform over time. Track improvements in boundaries, reciprocity, and connection quality.',
      color: 'from-green-500 to-emerald-500',
      emoji: '📊',
      vibe: 'See your progress clearly',
    },
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'Start without email, keep your data local, or securely sync across devices. You own your journey, always.',
      color: 'from-blue-500 to-indigo-500',
      emoji: '🔒',
      vibe: 'Your data, your control',
    },
  ];
  
  return (
    <section className="py-20 bg-gradient-to-br from-slate-800/50 to-teal-900/30 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-block mb-4"
          >
            <div className="text-6xl mb-4">🚀</div>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            What Friendship Vibe Actually Does
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Get a complete friendship toolkit: discover why you attract certain people, decode your squad dynamics, set boundaries without the guilt, and build skills for friendships that actually feel good.

          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="h-full"
            >
              <Card className="p-6 h-full hover-lift bg-gradient-to-br from-white/5 to-white/10 border-teal-500/20 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                  <div className={`w-full h-full rounded-full bg-gradient-to-br ${feature.color}`} />
                </div>
                
                <div className="relative space-y-4">
                  {/* Icon with gradient */}
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div 
                      className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg`}
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <feature.icon className="w-7 h-7 text-white" />
                    </motion.div>
                    <span className="text-3xl">{feature.emoji}</span>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {feature.title}
                    </h3>
                    
                    <p className="text-slate-300 mb-4 leading-relaxed">
                      {feature.description}
                    </p>
                    
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-400" />
                      <span className="text-teal-300 text-sm font-medium italic">
                        {feature.vibe}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Bottom stats row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          {[
            { stat: '26', label: 'Assessment Questions', subtext: 'No gaming the system' },
            { stat: '8', label: 'Learning Modules', subtext: 'Practical, not preachy' },
            { stat: '8', label: 'Interactive Tools', subtext: 'Practice makes permanent' },
            { stat: '6', label: 'Friendship Styles', subtext: 'A mix, not a label' },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.9 + index * 0.1 }}
              className="group"
            >
              <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-coral-400 mb-2 group-hover:from-coral-400 group-hover:to-gold-400 transition-all">
                {item.stat}
              </div>
              <div className="text-white font-semibold mb-1">
                {item.label}
              </div>
              <div className="text-slate-400 text-sm">
                {item.subtext}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.2 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-teal-900/40 to-coral-900/40 border border-teal-500/30 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Understand Your Friendship Patterns? 💜
            </h3>
            <p className="text-teal-200 mb-6">
              Most friendship problems aren't about the friends - they're about the patterns. 
              Time to understand yours.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400" />
                <span>Research-backed</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span>No name or email needed</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-purple-400" />
                <span>Genuine insights</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FriendshipVibeFeatures;